/**
 * Composable finance domain.
 *
 * staleTime: 30 detik untuk list, 5 menit untuk detail/summary.
 *
 * Pemakaian:
 *   const { data, isLoading } = useUtangAdmin({ page: 1, limit: 10 });
 *   const payHpp = usePayHpp();
 *   await payHpp.mutateAsync(payload);
 */
import { computed, unref } from "@vue/composition-api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { keys } from "@/queries/keys";
import {
  fetchUtangAdmin,
  fetchUtangAdminById,
  updateUtangAdmin,
  fetchUtangMitra,
  fetchUtangMitraById,
  fetchUtangMitraSummary,
  createUtangMitra,
  updateUtangMitra,
  exportUtangMitra,
  importUtangMitra,
  fetchInvoiceAgen,
  fetchInvoiceAgenLatest,
  fetchInvoiceMitra,
  fetchPiutangAgen,
  fetchPiutangAgenCount,
  fetchPiutangAgenCountAll,
  payPiutangAgen,
  exportPiutangAgen,
  importPiutangAgen,
  fetchPiutangMember,
  fetchPiutangMemberCount,
  fetchPiutangMemberCountAll,
  fetchPiutangMemberDetailList,
  payPiutangMember,
  exportPiutangMember,
  importPiutangMember,
  fetchBayarHpp,
  fetchBayarHppDetailList,
  payHpp,
  exportHpp,
  importHpp,
  fetchBiayaOperasional,
  fetchBiayaOperasionalById,
  fetchBiayaOperasionalTotal,
  fetchBiayaOperasionalTotalBranch,
  createBiayaOperasional,
  updateBiayaOperasional,
  deleteBiayaOperasional,
  importBiayaOperasional,
  fetchTagihanAgen,
  fetchTagihanAgenByAgen,
} from "@/queries/api/finance";

const LIST_STALE = 30 * 1000;
const DETAIL_STALE = 5 * 60 * 1000;

function p(input) {
  return unref(input);
}

/* ============================================================
 * Utang Admin
 * ============================================================ */

export function useUtangAdmin(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.utangAdmin(p(params))),
    queryFn: () => fetchUtangAdmin(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useUtangAdminDetail(id, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "utang-admin", "detail", p(id)]),
    queryFn: () => fetchUtangAdminById(p(id)),
    staleTime: DETAIL_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

export function useUpdateUtangAdmin(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateUtangAdmin,
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...keys.finance.all, "utang-admin"],
      }),
    ...options,
  });
}

/* ============================================================
 * Utang Mitra
 * ============================================================ */

export function useUtangMitra(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.utangMitra(p(params))),
    queryFn: () => fetchUtangMitra(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useUtangMitraDetail(id, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "utang-mitra", "detail", p(id)]),
    queryFn: () => fetchUtangMitraById(p(id)),
    staleTime: DETAIL_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

export function useUtangMitraSummary(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "utang-mitra", "summary", p(params)]),
    queryFn: () => fetchUtangMitraSummary(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useCreateUtangMitra(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createUtangMitra,
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...keys.finance.all, "utang-mitra"],
      }),
    ...options,
  });
}

export function useUpdateUtangMitra(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateUtangMitra,
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...keys.finance.all, "utang-mitra"],
      }),
    ...options,
  });
}

export function useExportUtangMitra(options = {}) {
  return useMutation({
    mutationFn: ({ params, config } = {}) => exportUtangMitra(params, config),
    ...options,
  });
}

export function useImportUtangMitra(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, config }) => importUtangMitra(payload, config),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...keys.finance.all, "utang-mitra"],
      }),
    ...options,
  });
}

/* ============================================================
 * Invoice Agen & Mitra
 * ============================================================ */

export function useInvoiceAgen(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.invoiceAgen(p(params))),
    queryFn: () => fetchInvoiceAgen(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInvoiceAgenLatest(options = {}) {
  return useQuery({
    queryKey: [...keys.finance.all, "invoice-agen", "latest"],
    queryFn: fetchInvoiceAgenLatest,
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInvoiceMitra(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.invoiceMitra(p(params))),
    queryFn: () => fetchInvoiceMitra(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

/* ============================================================
 * Piutang Agen
 * ============================================================ */

export function usePiutangAgen(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.piutangAgen(p(params))),
    queryFn: () => fetchPiutangAgen(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePiutangAgenCount(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "piutang-agen", "count", p(params)]),
    queryFn: () => fetchPiutangAgenCount(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePiutangAgenCountAll(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "piutang-agen", "count-all", p(params)]),
    queryFn: () => fetchPiutangAgenCountAll(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePayPiutangAgen(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: payPiutangAgen,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...keys.finance.all, "piutang-agen"] });
      qc.invalidateQueries({ queryKey: keys.international.all });
    },
    ...options,
  });
}

export function useExportPiutangAgen(options = {}) {
  return useMutation({
    mutationFn: ({ params, config } = {}) => exportPiutangAgen(params, config),
    ...options,
  });
}

export function useImportPiutangAgen(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, config }) => importPiutangAgen(payload, config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...keys.finance.all, "piutang-agen"] });
      qc.invalidateQueries({ queryKey: keys.international.all });
    },
    ...options,
  });
}

/* ============================================================
 * Piutang Member
 * ============================================================ */

export function usePiutangMember(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.piutangMember(p(params))),
    queryFn: () => fetchPiutangMember(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePiutangMemberCount(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "piutang-member", "count", p(params)]),
    queryFn: () => fetchPiutangMemberCount(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePiutangMemberCountAll(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "piutang-member", "count-all", p(params)]),
    queryFn: () => fetchPiutangMemberCountAll(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePiutangMemberDetailList(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "piutang-member", "detail-list", p(params)]),
    queryFn: () => fetchPiutangMemberDetailList(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePayPiutangMember(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: payPiutangMember,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...keys.finance.all, "piutang-member"] });
      qc.invalidateQueries({ queryKey: keys.international.all });
    },
    ...options,
  });
}

export function useExportPiutangMember(options = {}) {
  return useMutation({
    mutationFn: ({ params, config } = {}) => exportPiutangMember(params, config),
    ...options,
  });
}

export function useImportPiutangMember(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, config }) => importPiutangMember(payload, config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...keys.finance.all, "piutang-member"] });
    },
    ...options,
  });
}

/* ============================================================
 * Bayar HPP
 * ============================================================ */

export function useBayarHpp(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.bayarHpp(p(params))),
    queryFn: () => fetchBayarHpp(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useBayarHppDetailList(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.finance.all, "bayar-hpp", "detail-list", p(params)]),
    queryFn: () => fetchBayarHppDetailList(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePayHpp(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: payHpp,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...keys.finance.all, "bayar-hpp"] });
      qc.invalidateQueries({ queryKey: keys.international.all });
    },
    ...options,
  });
}

export function useExportHpp(options = {}) {
  return useMutation({
    mutationFn: ({ params, config } = {}) => exportHpp(params, config),
    ...options,
  });
}

export function useImportHpp(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, config }) => importHpp(payload, config),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...keys.finance.all, "bayar-hpp"] }),
    ...options,
  });
}

/* ============================================================
 * Biaya Operasional
 * ============================================================ */

export function useBiayaOperasional(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.biayaOperasional(p(params))),
    queryFn: () => fetchBiayaOperasional(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useBiayaOperasionalDetail(id, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.finance.all,
      "biaya-operasional",
      "detail",
      p(id),
    ]),
    queryFn: () => fetchBiayaOperasionalById(p(id)),
    staleTime: DETAIL_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

export function useBiayaOperasionalTotal(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.finance.all,
      "biaya-operasional",
      "total",
      p(params),
    ]),
    queryFn: () => fetchBiayaOperasionalTotal(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useBiayaOperasionalTotalBranch(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.finance.all,
      "biaya-operasional",
      "total-branch",
      p(params),
    ]),
    queryFn: () => fetchBiayaOperasionalTotalBranch(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

function invalidateBiayaOperasional(qc) {
  return qc.invalidateQueries({
    queryKey: [...keys.finance.all, "biaya-operasional"],
  });
}

export function useCreateBiayaOperasional(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBiayaOperasional,
    onSuccess: () => invalidateBiayaOperasional(qc),
    ...options,
  });
}

export function useUpdateBiayaOperasional(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateBiayaOperasional,
    onSuccess: () => invalidateBiayaOperasional(qc),
    ...options,
  });
}

export function useDeleteBiayaOperasional(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteBiayaOperasional,
    onSuccess: () => invalidateBiayaOperasional(qc),
    ...options,
  });
}

export function useImportBiayaOperasional(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, config }) => importBiayaOperasional(payload, config),
    onSuccess: () => invalidateBiayaOperasional(qc),
    ...options,
  });
}

/* ============================================================
 * Tagihan Agen
 * ============================================================ */

export function useTagihanAgen(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.finance.tagihanAgen(p(params))),
    queryFn: () => fetchTagihanAgen(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useTagihanAgenByAgen(agenId, params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.finance.all,
      "tagihan-agen",
      "by-agen",
      p(agenId),
      p(params),
    ]),
    queryFn: () => fetchTagihanAgenByAgen(p(agenId), p(params)),
    staleTime: LIST_STALE,
    enabled: computed(() => !!p(agenId)),
    ...options,
  });
}
